import { type CSSProperties, type ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { WIDTH_TO_COLS, type WidgetWidth } from "@playbookai/types";
import { cn } from "../../lib/utils.js";
import { Card } from "../../molecules/Card/index.js";
import { DND_TRANSITION } from "../../motion.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import { getWidget } from "../../widgets/registry.js";
import { CardChrome } from "./CardChrome.js";
import { WidgetErrorBoundary, WidgetSlot } from "./WidgetErrorBoundary.js";
import { COL_SPAN_CLASS, widgetLabel } from "./helpers.js";
import type { DashboardItem } from "./types.js";

export interface SortableCardProps {
  item: DashboardItem;
  index: number;
  total: number;
  canReorder: boolean;
  renderWidget: (item: DashboardItem) => ReactNode;
  onChangeWidth?:
    | ((instanceId: string, width: WidgetWidth) => void)
    | undefined;
  onRemove?: ((instanceId: string) => void) | undefined;
  onMove?: ((instanceId: string, dir: -1 | 1) => void) | undefined;
  onEdit?: ((instanceId: string) => void) | undefined;
}

/** One sortable grid cell in edit mode: outer chrome + a body rendered through
 *  the boundary, with the inner frame neutralized so the chrome owns the card. */
export function SortableCard({
  item,
  index,
  total,
  canReorder,
  renderWidget,
  onChangeWidth,
  onRemove,
  onMove,
  onEdit,
}: SortableCardProps) {
  /* The settle after a reorder is written inline by dnd-kit, on its own
     `200ms ease` clock. `DND_TRANSITION` puts it on the design system's state
     grade instead, and `null` removes it for a reader who has asked for
     reduced motion — the class below still guards the same case in CSS
     (ADR-0091). */
  const reducedMotion = useReducedMotion();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.instanceId,
    disabled: !canReorder,
    transition: reducedMotion ? null : DND_TRANSITION,
  });
  // Edit is only meaningful for kinds with an editor or a title override.
  const meta = getWidget(item.kind)?.dashboard;
  const canEdit =
    !!onEdit && (!!meta?.EditorForm || !!meta?.supportsTitleOverride);
  /* Translate only. `rectSortingStrategy` also sends scaleX/scaleY so a card
     matches the size of the slot it moves into; on this grid widths and
     heights differ, so the scale stretched cards and their text mid-drag. */
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  const cols = WIDTH_TO_COLS[item.width];
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-widget-kind={item.kind}
      data-widget-width={item.width}
      className={cn(
        "min-w-0 motion-reduce:!transition-none",
        COL_SPAN_CLASS[cols],
        isDragging && "opacity-40",
      )}
    >
      {/* The default Card: its 14px corner, border, fill and hairline (the
          hairline already overrode the old shadow-sm). */}
      <Card className="h-full flex flex-col">
        <CardChrome
          item={item}
          index={index}
          total={total}
          canReorder={canReorder}
          handleProps={{ ...attributes, ...listeners }}
          onChangeWidth={onChangeWidth}
          onRemove={onRemove}
          onMove={onMove}
          onEdit={canEdit ? onEdit : undefined}
        />
        <div className="flex-1 min-h-0 overflow-auto pointer-events-none text-pb-text [&>*]:!border-0 [&>*]:!rounded-none [&>*]:!shadow-none">
          <WidgetErrorBoundary
            widgetName={widgetLabel(item)}
            resetKeys={[item.instanceId, item.params]}
          >
            <WidgetSlot item={item} renderWidget={renderWidget} />
          </WidgetErrorBoundary>
        </div>
      </Card>
    </div>
  );
}
